import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import PayrollAdjustmentsPanel from '../components/payroll/PayrollAdjustmentsPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Download, 
  DollarSign, 
  Users,
  Clock,
  Calendar,
  Loader2,
  FileSpreadsheet,
  RefreshCw,
  Briefcase,
  UserCheck,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

function PayrollPeriodRegister({
  api,
  onOpenPeriod,
}) {
  const [periods, setPeriods] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");


  const loadPeriods = React.useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const response = await api.get(
          "/payroll/batches/register?engagement_type=all&state=NSW&limit=100"
        );

        setPeriods(
          response.data?.periods || []
        );

      } catch (exc) {
        const detail =
          exc?.response?.data?.detail;

        setError(
          detail?.message ||
          detail ||
          exc?.message ||
          "Unable to load payroll register."
        );

      } finally {
        setLoading(false);
      }
    },
    [api]
  );


  React.useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);


  function money(value) {
    return new Intl.NumberFormat(
      "en-AU",
      {
        style: "currency",
        currency: "AUD",
      }
    ).format(
      Number(value || 0)
    );
  }


  function dt(value) {
    if (!value) return "—";

    const d = new Date(value);

    return Number.isNaN(
      d.getTime()
    )
      ? String(value)
      : d.toLocaleString(
          "en-AU"
        );
  }


  function statusStyle(status) {
    const base = {
      display: "inline-flex",
      borderRadius: 999,
      padding: "4px 8px",
      fontSize: 11,
      fontWeight: 700,
    };

    if (
      status === "exported" ||
      status === "finalised"
    ) {
      return {
        ...base,
        background: "#ecfdf5",
        color: "#047857",
      };
    }

    if (
      status === "reopened"
    ) {
      return {
        ...base,
        background: "#fff7ed",
        color: "#c2410c",
      };
    }

    return {
      ...base,
      background: "#f3f4f6",
      color: "#d4d4d8",
    };
  }


  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div
        style={{
          background: "#18181b",
          border: "1px solid #3f3f46",
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "18px 20px",
            borderBottom:
              "1px solid #3f3f46",
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                color: "#f4f4f5",
              }}
            >
              Payroll Period Register
            </h2>

            <p
              style={{
                margin:
                  "5px 0 0",
                color: "#a1a1aa",
                fontSize: 13,
              }}
            >
              Historical payroll periods,
              latest revision and export status.
            </p>
          </div>

          <button
            type="button"
            onClick={loadPeriods}
            disabled={loading}
            className="px-3 py-2 rounded border text-sm"
          >
            {loading
              ? "Loading..."
              : "Refresh"}
          </button>
        </div>

        {error && (
          <div
            style={{
              margin: 16,
              padding: 12,
              borderRadius: 8,
              background: "#fef2f2",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            overflowX: "auto",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse:
                "collapse",
              fontSize: 13,
            }}
          >
            <thead>
              <tr
                style={{
                  background:
                    "#f9fafb",
                }}
              >
                {[
                  "Period",
                  "Status",
                  "Latest Rev",
                  "Revisions",
                  "Hours",
                  "Gross Pay",
                  "Adjusted",
                  "Finalised",
                  "Exported",
                  "Export Reference",
                  "",
                ].map(
                  (heading) => (
                    <th
                      key={heading}
                      style={{
                        padding:
                          "10px 12px",
                        textAlign:
                          "left",
                        whiteSpace:
                          "nowrap",
                        color:
                          "#6b7280",
                        fontSize: 11,
                      }}
                    >
                      {heading}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>
              {periods.map(
                (item) => (
                  <tr
                    key={
                      item.batch_id ||
                      `${item.period_start}-${item.period_end}`
                    }
                    style={{
                      borderTop:
                        "1px solid #3f3f46",
                    }}
                  >
                    <td
                      style={{
                        padding:
                          "12px",
                        fontWeight:
                          700,
                        color:
                          "#f4f4f5",
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {item.period_start}
                      {" → "}
                      {item.period_end}
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                      }}
                    >
                      <span
                        style={statusStyle(
                          item.status
                        )}
                      >
                        {String(
                          item.status ||
                          "draft"
                        ).toUpperCase()}
                      </span>
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                      }}
                    >
                      {item.latest_revision ||
                        "—"}
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                      }}
                    >
                      {item.revision_count ||
                        0}
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                      }}
                    >
                      {Number(
                        item.hours || 0
                      ).toFixed(2)}
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                        fontWeight:
                          700,
                      }}
                    >
                      {money(
                        item.pay
                      )}
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                      }}
                    >
                      {item.adjusted_line_count ??
                        "—"}
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {dt(
                        item.finalised_at
                      )}
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {dt(
                        item.exported_at
                      )}
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                        maxWidth:
                          260,
                        wordBreak:
                          "break-word",
                      }}
                    >
                      {item.export_reference ||
                        "—"}
                    </td>

                    <td
                      style={{
                        padding:
                          "12px",
                      }}
                    >
                      <button
                        type="button"
                        className="px-3 py-2 rounded border text-sm"
                        onClick={() =>
                          onOpenPeriod(
                            item
                          )
                        }
                      >
                        Open Payroll
                      </button>
                    </td>
                  </tr>
                )
              )}

              {!loading &&
                periods.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan="11"
                      style={{
                        padding:
                          "24px",
                        textAlign:
                          "center",
                        color:
                          "#6b7280",
                      }}
                    >
                      No historical payroll
                      periods recorded yet.
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


export default function PayrollPage() {
  const { api, user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('contractor');

  const openHistoricalPayroll = (period) => {
    if (!period) return;

    setStartDate(
      period.period_start
    );

    setEndDate(
      period.period_end
    );

    setActiveTab(
      'adjustments'
    );

    toast.info(
      `Opened payroll ${period.period_start} to ${period.period_end}`
    );
  };
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Contractor state
  const [contractorPreview, setContractorPreview] = useState(null);
  
  // Staff state
  const [staffPreview, setStaffPreview] = useState(null);
  const [staffTypeFilter, setStaffTypeFilter] = useState('all');

  // Default to current week
  const getWeekDates = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)); // Monday
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
    
    return {
      start: startOfWeek.toISOString().split('T')[0],
      end: endOfWeek.toISOString().split('T')[0]
    };
  };

  const defaultDates = getWeekDates();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);

  const fetchContractorPreview = useCallback(async () => {
    if (!startDate || !endDate) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/contractor-rates/contractor-preview?start=${startDate}&end=${endDate}`);
      setContractorPreview(response.data);
    } catch (error) {
      console.error('Error fetching contractor preview:', error);
      toast.error('Failed to load contractor payroll');
    } finally {
      setLoading(false);
    }
  }, [api, startDate, endDate]);

  const fetchStaffPreview = useCallback(async () => {
    if (!startDate || !endDate) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/payroll/staff/preview?start=${startDate}&end=${endDate}&employee_type=${staffTypeFilter}`);
      setStaffPreview(response.data);
    } catch (error) {
      console.error('Error fetching staff preview:', error);
      toast.error('Failed to load staff payroll');
    } finally {
      setLoading(false);
    }
  }, [api, startDate, endDate, staffTypeFilter]);

  useEffect(() => {
    if (activeTab === 'contractor') {
      fetchContractorPreview();
    } else {
      fetchStaffPreview();
    }
  }, [activeTab, fetchContractorPreview, fetchStaffPreview, staffTypeFilter]);

  const handleContractorExport = async () => {
    setActiveTab('adjustments');

    toast.info(
      'Finalise and export payroll from Adjustments & Approval.'
    );
  };

  const handleStaffExport = async () => {
    setActiveTab('adjustments');

    toast.info(
      'Finalise and export payroll from Adjustments & Approval.'
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white">Payroll</h1>
          <p className="text-zinc-400 text-sm">Manage payroll for contractors and staff</p>
        </div>
      </div>

      {/* Date Range Selector */}
      <Card className="bg-[#1a1a1d] border-[#3f3f46]">
        <CardContent className="pt-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] w-[160px]"
                data-testid="payroll-start-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] w-[160px]"
                data-testid="payroll-end-date"
              />
            </div>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => {
                const dates = getWeekDates();
                setStartDate(dates.start);
                setEndDate(dates.end);
              }}
              className="border-zinc-600"
            >
              <Calendar className="w-4 h-4 mr-2" />
              This Week
            </Button>
            <Button
              onClick={() => activeTab === 'contractor' ? fetchContractorPreview() : fetchStaffPreview()}
              disabled={loading}
              size="sm"
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="payroll-refresh-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#18181b] border border-[#3f3f46]">
          <TabsTrigger 
            value="contractor" 
            className="data-[state=active]:bg-[#3f3f46]"
            data-testid="payroll-tab-contractor"
          >
            <Briefcase className="w-4 h-4 mr-2" />
            Contractors
          </TabsTrigger>
          <TabsTrigger 
            value="permanent" 
            className="data-[state=active]:bg-[#3f3f46]"
            data-testid="payroll-tab-permanent"
          >
            <UserCheck className="w-4 h-4 mr-2" />
            Permanent Staff
          </TabsTrigger>
          <TabsTrigger 
            value="casual" 
            className="data-[state=active]:bg-[#3f3f46]"
            data-testid="payroll-tab-casual"
          >
            <Users className="w-4 h-4 mr-2" />
            Casual Staff
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger
              value="register"
              className="data-[state=active]:bg-[#3f3f46]"
              data-testid="payroll-tab-register"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Period Register
            </TabsTrigger>
          )}

          {isAdmin && (
            <TabsTrigger
              value="adjustments"
              className="data-[state=active]:bg-[#3f3f46]"
              data-testid="payroll-tab-adjustments"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Adjustments & Approval
            </TabsTrigger>
          )}
        </TabsList>

        {/* Contractor Tab */}
        <TabsContent value="contractor" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
          ) : contractorPreview ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-[#1a1a1d] border-[#3f3f46]">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <Users className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Contractors</p>
                        <p className="text-lg font-bold text-white">{contractorPreview.contractor_count || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-[#1a1a1d] border-[#3f3f46]">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-amber-500/20">
                        <Clock className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Total Hours</p>
                        <p className="text-lg font-bold text-white">{contractorPreview.summary?.total_hours?.toFixed(1) || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-[#1a1a1d] border-[#3f3f46]">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <DollarSign className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Total Payable</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(contractorPreview.summary?.total_amount)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-[#1a1a1d] border-[#3f3f46]">
                  <CardContent className="pt-4">
                    <Button 
                      onClick={handleContractorExport}
                      disabled={exporting}
                      className="w-full bg-emerald-500 hover:bg-emerald-600"
                      data-testid="contractor-export-btn"
                    >
                      {exporting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Finalise & Export
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Contractor Table */}
              <Card className="bg-[#1a1a1d] border-[#3f3f46]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-rajdhani text-lg">Contractor Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#3f3f46]">
                          <th className="text-left py-3 px-2 text-zinc-400 font-medium">Contractor</th>
                          <th className="text-right py-3 px-2 text-zinc-400 font-medium">Officers</th>
                          <th className="text-right py-3 px-2 text-zinc-400 font-medium">Hours</th>
                          <th className="text-right py-3 px-2 text-zinc-400 font-medium">Rate</th>
                          <th className="text-right py-3 px-2 text-zinc-400 font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contractorPreview.contractors?.map((c, idx) => (
                          <tr key={idx} className="border-b border-[#3f3f46]/50 hover:bg-[#27272a]">
                            <td className="py-3 px-2">
                              <span className="text-white">{c.company_name}</span>
                              {c.abn && <span className="text-xs text-zinc-500 ml-2">ABN: {c.abn}</span>}
                            </td>
                            <td className="py-3 px-2 text-right text-zinc-300">{c.officer_count || 0}</td>
                            <td className="py-3 px-2 text-right text-zinc-300">{c.total_hours?.toFixed(1) || 0}</td>
                            <td className="py-3 px-2 text-right text-zinc-300">{formatCurrency(c.hourly_rate)}/hr</td>
                            <td className="py-3 px-2 text-right font-medium text-emerald-400">{formatCurrency(c.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(!contractorPreview.contractors || contractorPreview.contractors.length === 0) && (
                      <div className="text-center py-8 text-zinc-500">
                        No contractor data for this period
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12 text-zinc-500">
              Select a date range and click Refresh to load payroll data
            </div>
          )}
        </TabsContent>

        {/* Permanent Staff Tab */}
        <TabsContent value="permanent" className="mt-4">
          <StaffPayrollContent
            staffPreview={staffPreview}
            loading={loading}
            exporting={exporting}
            onExport={handleStaffExport}
            formatCurrency={formatCurrency}
            filterType="employee"
            onRefresh={() => { setStaffTypeFilter('employee'); fetchStaffPreview(); }}
          />
        </TabsContent>

        {/* Casual Staff Tab */}
        <TabsContent value="casual" className="mt-4">
          <StaffPayrollContent
            staffPreview={staffPreview}
            loading={loading}
            exporting={exporting}
            onExport={handleStaffExport}
            formatCurrency={formatCurrency}
            filterType="casual"
            onRefresh={() => { setStaffTypeFilter('casual'); fetchStaffPreview(); }}
          />
        </TabsContent>
        {isAdmin && (
          <TabsContent
            value="register"
            className="mt-4"
          >
            <PayrollPeriodRegister
              api={api}
              onOpenPeriod={
                openHistoricalPayroll
              }
            />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent
            value="adjustments"
            className="mt-4"
          >
            <PayrollAdjustmentsPanel
              api={api}
              start={startDate}
              end={endDate}
              engagementType="all"
              state="NSW"
              currentUser={user}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// Staff Payroll Content Component
function StaffPayrollContent({ staffPreview, loading, exporting, onExport, formatCurrency, filterType, onRefresh }) {
  useEffect(() => {
    onRefresh();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!staffPreview) {
    return (
      <div className="text-center py-12 text-zinc-500">
        Loading staff payroll data...
      </div>
    );
  }

  const filteredStaff = staffPreview.staff?.filter(s => 
    filterType === 'all' || s.employee_type === filterType
  ) || [];

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Staff</p>
                <p className="text-lg font-bold text-white">{filteredStaff.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Regular Hours</p>
                <p className="text-lg font-bold text-white">{staffPreview.summary?.regular_hours?.toFixed(1) || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <TrendingUp className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Overtime</p>
                <p className="text-lg font-bold text-white">{staffPreview.summary?.overtime_hours?.toFixed(1) || 0}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Total Pay</p>
                <p className="text-lg font-bold text-white">{formatCurrency(staffPreview.summary?.total_pay)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="pt-4">
            <Button 
              onClick={onExport}
              disabled={exporting}
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              data-testid="staff-export-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Finalise & Export
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Staff Table */}
      <Card className="bg-[#1a1a1d] border-[#3f3f46]">
        <CardHeader className="pb-2">
          <CardTitle className="font-rajdhani text-lg">
            {filterType === 'employee' ? 'Permanent' : 'Casual'} Staff Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#3f3f46]">
                  <th className="text-left py-3 px-2 text-zinc-400 font-medium">Employee</th>
                  <th className="text-center py-3 px-2 text-zinc-400 font-medium">ID</th>
                  <th className="text-right py-3 px-2 text-zinc-400 font-medium">Regular</th>
                  <th className="text-right py-3 px-2 text-zinc-400 font-medium">OT</th>
                  <th className="text-right py-3 px-2 text-zinc-400 font-medium">Weekend</th>
                  <th className="text-right py-3 px-2 text-zinc-400 font-medium">Holiday</th>
                  <th className="text-right py-3 px-2 text-zinc-400 font-medium">Total Pay</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((staff, idx) => (
                  <tr key={idx} className="border-b border-[#3f3f46]/50 hover:bg-[#27272a]">
                    <td className="py-3 px-2">
                      <span className="text-white">{staff.name}</span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      {staff.employee_id ? (
                        <Badge variant="outline" className="border-zinc-600 text-xs">
                          {staff.employee_id}
                        </Badge>
                      ) : (
                        <span className="text-zinc-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right text-zinc-300">{staff.hours.regular.toFixed(1)}h</td>
                    <td className="py-3 px-2 text-right">
                      {staff.hours.overtime > 0 ? (
                        <span className="text-orange-400">{staff.hours.overtime.toFixed(1)}h</span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right">
                      {staff.hours.weekend > 0 ? (
                        <span className="text-amber-400">{staff.hours.weekend.toFixed(1)}h</span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right">
                      {staff.hours.public_holiday > 0 ? (
                        <span className="text-purple-400">{staff.hours.public_holiday.toFixed(1)}h</span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right font-medium text-emerald-400">
                      {formatCurrency(staff.pay.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredStaff.length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No {filterType === 'employee' ? 'permanent' : 'casual'} staff data for this period
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
